import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

interface AnalyzingModalProps {
  name: string;
  gender: string;
  birthday: string;
  amount: number;
  orderNo: string;
  onComplete: () => void;
  onError: () => void;
  payPromise: Promise<{ pay_url?: string; wechat_link?: string; error?: string }>;
}

const STEPS = [
  { icon: "ri-search-eye-line", text: "正在解析八字命盘...", duration: 1200 },
  { icon: "ri-bar-chart-grouped-line", text: "分析五行能量分布...", duration: 1400 },
  { icon: "ri-heart-3-line", text: "推算姻缘运势走向...", duration: 1300 },
  { icon: "ri-star-line", text: "匹配专属命理师...", duration: 1100 },
  { icon: "ri-file-text-line", text: "生成个人测算报告...", duration: 1000 },
];

export default function AnalyzingModal({
  name,
  gender,
  birthday,
  amount,
  orderNo,
  onComplete,
  onError,
  payPromise,
}: AnalyzingModalProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [progress, setProgress] = useState(0);
  const [payReady, setPayReady] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const payUrlRef = useRef<string | null>(null);
  const allDoneRef = useRef(false);

  const wechatLinkRef = useRef<string | null>(null);

  // 监听支付接口结果
  useEffect(() => {
    let isMounted = true;
    
    payPromise.then((result) => {
      if (!isMounted) return;
      if (result.pay_url) {
        payUrlRef.current = result.pay_url;
        wechatLinkRef.current = result.wechat_link || "";
        setPayReady(true);
      } else {
        payUrlRef.current = "error";
        setPayReady(true);
      }
    }).catch(() => {
      if (!isMounted) return;
      payUrlRef.current = "error";
      setPayReady(true);
    });
    
    return () => { isMounted = false; };
  }, [payPromise]);

  // 步骤动画
  useEffect(() => {
    let stepIndex = 0;
    let totalTime = 0;
    const totalDuration = STEPS.reduce((s, st) => s + st.duration, 0);

    const runStep = (idx: number) => {
      if (idx >= STEPS.length) {
        // 所有步骤完成
        setProgress(100);
        allDoneRef.current = true;
        setAllDone(true);
        return;
      }
      setCurrentStep(idx);
      const stepDuration = STEPS[idx].duration;
      totalTime += stepDuration;

      // 进度条动画
      const startProgress = Math.round(((totalTime - stepDuration) / totalDuration) * 95);
      const endProgress = Math.round((totalTime / totalDuration) * 95);
      let frame = 0;
      const frames = Math.floor(stepDuration / 16);
      const progressTimer = setInterval(() => {
        frame++;
        const p = startProgress + Math.round(((endProgress - startProgress) * frame) / frames);
        setProgress(Math.min(p, 95));
        if (frame >= frames) clearInterval(progressTimer);
      }, 16);

      const timer = setTimeout(() => {
        setCompletedSteps((prev) => [...prev, idx]);
        runStep(idx + 1);
      }, stepDuration);

      return () => {
        clearTimeout(timer);
        clearInterval(progressTimer);
      };
    };

    runStep(stepIndex);
  }, []);

  // 动画完成 + 支付接口都好了 => 跳转到 /pay 中间页
  useEffect(() => {
    if (allDone && payReady) {
      const url = payUrlRef.current;
      const wechatLink = wechatLinkRef.current;
      setProgress(100);
      setTimeout(() => {
        // 存储支付链接（即使失败也存空字符串）
        localStorage.setItem("pay_url", (!url || url === "error") ? "" : url);
        if (wechatLink) {
          localStorage.setItem("wechat_link", wechatLink);
        }
        // 存储金额到 localStorage
        localStorage.setItem("pay_amount", String(amount));
        // 存储订单号到 localStorage，供支付页面轮询使用
        localStorage.setItem("pay_order_no", orderNo);
        const params = new URLSearchParams({
          name: name || "",
          gender: gender || "",
          birthday: birthday || "",
          order_no: orderNo,
        });
        const targetUrl = `/pay?${params.toString()}`;
        navigate(targetUrl, { replace: true });
        onComplete();
      }, 400);
    }
  }, [allDone, payReady, onComplete, navigate, name, gender, birthday, amount]);

  const displayName = name || "您";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }}
    >
      <style>{`
        @keyframes spinRing {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spinRingReverse {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.08); }
        }
        @keyframes floatUp {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmerBar {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes stepIn {
          0% { opacity: 0; transform: translateX(-12px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        .spin-ring { animation: spinRing 3s linear infinite; }
        .spin-ring-rev { animation: spinRingReverse 2s linear infinite; }
        .pulse-glow { animation: pulseGlow 2s ease-in-out infinite; }
        .float-up { animation: floatUp 0.5s ease both; }
        .step-in { animation: stepIn 0.4s ease both; }
        .shimmer-bar {
          background: linear-gradient(90deg, #c8922a 0%, #e8b84b 40%, #fff8e1 50%, #e8b84b 60%, #c8922a 100%);
          background-size: 200% auto;
          animation: shimmerBar 1.8s linear infinite;
        }
      `}</style>

      <div
        className="w-full mx-4 rounded-2xl overflow-hidden float-up"
        style={{
          maxWidth: 360,
          background: "linear-gradient(160deg, #1a0000 0%, #3d0808 40%, #5c1212 100%)",
          border: "2px solid #c8922a",
          boxShadow: "0 0 0 4px rgba(200,146,42,0.15), 0 30px 80px rgba(0,0,0,0.6)",
        }}
      >
        {/* 顶部金条 */}
        <div className="h-1.5 w-full shimmer-bar" />

        <div className="px-6 py-8">
          {/* 旋转光环 + 中心图标 */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative w-28 h-28 flex items-center justify-center">
              {/* 外环 */}
              <div
                className="absolute inset-0 rounded-full spin-ring"
                style={{
                  border: "2px dashed rgba(200,146,42,0.5)",
                }}
              />
              {/* 中环 */}
              <div
                className="absolute inset-3 rounded-full spin-ring-rev"
                style={{
                  border: "2px solid rgba(232,184,75,0.4)",
                  borderTopColor: "#e8b84b",
                }}
              />
              {/* 内圆 */}
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center pulse-glow"
                style={{
                  background: "linear-gradient(135deg, #8b1a1a 0%, #c0000a 100%)",
                  border: "2px solid #c8922a",
                  boxShadow: "0 0 20px rgba(192,0,10,0.6)",
                }}
              >
                <span style={{ fontSize: 28, lineHeight: 1 }}>🔮</span>
              </div>
            </div>
          </div>

          {/* 标题 */}
          <div className="text-center mb-1">
            <h2
              className="text-lg font-bold"
              style={{ color: "#e8b84b", fontFamily: "'Noto Serif SC', serif", letterSpacing: "0.1em" }}
            >
              正在为{displayName}测算姻缘
            </h2>
            <p className="text-xs mt-1" style={{ color: "rgba(232,184,75,0.6)" }}>
              {gender} · {birthday}
            </p>
          </div>

          {/* 分隔线 */}
          <div className="flex items-center gap-2 my-4">
            <div className="h-px flex-1" style={{ background: "linear-gradient(to right, transparent, rgba(200,146,42,0.5))" }} />
            <span style={{ color: "#c8922a", fontSize: 10 }}>✦</span>
            <div className="h-px flex-1" style={{ background: "linear-gradient(to left, transparent, rgba(200,146,42,0.5))" }} />
          </div>

          {/* 步骤列表 */}
          <div className="space-y-2.5 mb-6">
            {STEPS.map((step, i) => {
              const isDone = completedSteps.includes(i);
              const isActive = currentStep === i && !isDone;
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 step-in"
                  style={{
                    animationDelay: `${i * 0.08}s`,
                    opacity: i > currentStep ? 0.35 : 1,
                    transition: "opacity 0.4s ease",
                  }}
                >
                  {/* 状态图标 */}
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300"
                    style={{
                      background: isDone
                        ? "linear-gradient(135deg, #c8922a, #e8b84b)"
                        : isActive
                        ? "rgba(200,146,42,0.2)"
                        : "rgba(255,255,255,0.05)",
                      border: isDone
                        ? "none"
                        : isActive
                        ? "1.5px solid #c8922a"
                        : "1.5px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    {isDone ? (
                      <i className="ri-check-line" style={{ color: "#fff", fontSize: 13 }} />
                    ) : isActive ? (
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          background: "#c8922a",
                          animation: "pulseGlow 0.8s ease-in-out infinite",
                        }}
                      />
                    ) : (
                      <i className={step.icon} style={{ color: "rgba(255,255,255,0.2)", fontSize: 12 }} />
                    )}
                  </div>

                  {/* 文字 */}
                  <span
                    className="text-sm flex-1 transition-all duration-300"
                    style={{
                      color: isDone ? "#e8b84b" : isActive ? "#fff" : "rgba(255,255,255,0.3)",
                      fontFamily: "'Noto Serif SC', serif",
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {step.text}
                  </span>

                  {/* 完成标记 */}
                  {isDone && (
                    <span className="text-xs" style={{ color: "#c8922a" }}>完成</span>
                  )}
                  {isActive && (
                    <span className="text-xs" style={{ color: "rgba(232,184,75,0.7)", animation: "pulseGlow 1s ease-in-out infinite" }}>
                      分析中
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* 进度条 */}
          <div className="mb-3">
            <div
              className="w-full h-2.5 rounded-full overflow-hidden"
              style={{ background: "rgba(255,255,255,0.08)" }}
            >
              <div
                className="h-full rounded-full shimmer-bar transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-xs" style={{ color: "rgba(232,184,75,0.5)" }}>命盘解析中</span>
              <span className="text-xs font-bold" style={{ color: "#e8b84b" }}>{progress}%</span>
            </div>
          </div>

          {/* 底部提示 */}
          <p
            className="text-center text-xs"
            style={{ color: "rgba(232,184,75,0.45)", fontFamily: "'Noto Serif SC', serif" }}
          >
            专业命理师正在为您精准推算，请稍候...
          </p>
        </div>

        {/* 底部金条 */}
        <div className="h-1.5 w-full shimmer-bar" />
      </div>
    </div>
  );
}
